import React, { useEffect, useState } from "react";
import {
    PieChart as RechartsPieChart,
    Pie as RechartsPie,
    Cell as RechartsCell,
    BarChart as RechartsBarChart,
    Bar as RechartsBar,
    XAxis as RechartsXAxis,
    YAxis as RechartsYAxis,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend,
    ResponsiveContainer as RechartsResponsiveContainer,
} from "recharts";

import ApiServices from "../../shared/ApiServices/ApiServices";
import { RadiologistStats as RadiologistStatsType } from "./StatsTypes";
import {
    Activity as LucideActivity,
    CheckCircle2 as LucideCheckCircle2,
    Clock as LucideClock
} from "lucide-react";

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ElementType;
    borderColor: string;
}

const Chart = {
    PieChart: RechartsPieChart as any,
    Pie: RechartsPie as any,
    Cell: RechartsCell as any,
    BarChart: RechartsBarChart as any,
    Bar: RechartsBar as any,
    XAxis: RechartsXAxis as any,
    YAxis: RechartsYAxis as any,
    Tooltip: RechartsTooltip as any,
    Legend: RechartsLegend as any,
    ResponsiveContainer: RechartsResponsiveContainer as any,
};

const Icon = {
    Activity: LucideActivity as any,
    CheckCircle2: LucideCheckCircle2 as any,
    Clock: LucideClock as any,
};

// Status colors — US Healthcare Color Standard compliant
const COLORS = {
    assigned: '#045EB8',     // Clinical Blue — assigned / primary workflow
    pending: '#D97706',      // Amber-600 — WCAG AA compliant, caution / pending
    in_progress: '#7C3AED',  // Violet-600 — active / in-progress
    completed: '#16A34A',    // Green-600 — success / completed
    new: '#0D9488',          // Teal-600 — new / informational
    hold: '#DC2626',         // Red-600 — critical hold (US Healthcare standard)
};

// Modality colors — aligned with US radiology UI conventions
const MODALITY_COLORS: { [key: string]: string } = {
    CT: '#045EB8',           // Clinical Blue (core imaging modality)
    MRI: '#7C3AED',          // Violet (advanced imaging)
    MR: '#7C3AED',
    'X-RAY': '#D97706',      // Clinical Amber (traditional radiography)
    XRAY: '#D97706',
    ULTRASOUND: '#16A34A',   // Green (non-invasive / safe imaging)
    US: '#16A34A',
    OT: '#0D9488',           // Teal (procedural / operating)
    PET: '#EA580C',          // Orange-600 (nuclear imaging / metabolic)
    MAMMO: '#DB2777',        // Pink-600 (breast imaging awareness)
    FLUOROSCOPY: '#0891B2',  // Cyan-600 (real-time imaging)
};

const parseModalityString = (value: string): Array<{ modality: string; count: number }> => {
    if (!value || value === "0") return [];

    const parts = value.split(",").map(s => s.trim());
    const results: Array<{ modality: string; count: number }> = [];

    for (const part of parts) {
        const match = part.match(/^([a-zA-Z_-]+)-(\d+)$/);
        if (match) {
            const modalityName = match[1].toUpperCase().replace("_", "-");
            const count = parseInt(match[2], 10);
            if (count > 0) {
                results.push({ modality: modalityName, count });
            }
        }
    }

    return results;
};

/* Shimmer bar for RadiologistStats skeletons */
const RSShimmer = ({ className = '', delay = '0s', style }: { className?: string; delay?: string; style?: React.CSSProperties }) => (
    <div
        className={`relative overflow-hidden rounded ${className}`}
        style={{ backgroundColor: 'var(--v2-skeleton-base)', ...style }}
    >
        <div
            className="absolute inset-0 -translate-x-full animate-shimmer"
            style={{
                backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)',
                animationDelay: delay,
            }}
        />
    </div>
);

const StatCardSkeleton = () => (
    <div
        className="relative rounded-[var(--v2-radius-xl)] overflow-hidden"
        style={{
            background: 'var(--v2-bg-card)',
            border: '1px solid var(--v2-border-card)',
            boxShadow: 'var(--v2-shadow-card)',
        }}
    >
        <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: 'var(--v2-skeleton-base)' }} />
        <div className="flex items-center gap-3 px-4 py-3.5">
            <RSShimmer className="w-[34px] h-[34px] rounded-[var(--v2-radius-md)] flex-shrink-0" delay="0s" />
            <div className="flex-1 space-y-1.5">
                <RSShimmer className="h-2.5 w-20" delay="0.05s" />
                <RSShimmer className="h-2 w-28" delay="0.1s" />
            </div>
            <RSShimmer className="h-8 w-10 flex-shrink-0" delay="0.15s" />
        </div>
    </div>
);
/* Chart placeholder skeleton — shows axis hints + bars/circle */
const ChartSkeleton = ({ height = 'h-64', type = 'bar' }: { height?: string; type?: 'bar' | 'donut' }) => (
    <div
        className={`relative overflow-hidden rounded-lg ${height}`}
        style={{ backgroundColor: 'var(--v2-skeleton-base)', opacity: 0.5 }}
    >
        {/* Shimmer overlay */}
        <div
            className="absolute inset-0 -translate-x-full animate-shimmer"
            style={{
                backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)',
                animationDelay: '0.2s',
            }}
        />
        {/* Chart hint shapes */}
        <div className="absolute inset-0 flex items-end justify-center gap-4 p-6 pb-10">
            {type === 'bar' ? (
                <>
                    <div className="w-8 rounded-t" style={{ height: '40%', backgroundColor: 'var(--v2-skeleton-base)' }} />
                    <div className="w-8 rounded-t" style={{ height: '65%', backgroundColor: 'var(--v2-skeleton-base)' }} />
                    <div className="w-8 rounded-t" style={{ height: '45%', backgroundColor: 'var(--v2-skeleton-base)' }} />
                    <div className="w-8 rounded-t" style={{ height: '80%', backgroundColor: 'var(--v2-skeleton-base)' }} />
                    <div className="w-8 rounded-t" style={{ height: '55%', backgroundColor: 'var(--v2-skeleton-base)' }} />
                </>
            ) : (
                <div
                    className="w-32 h-32 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{ border: '12px solid var(--v2-skeleton-base)' }}
                />
            )}
        </div>
    </div>
);

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: CardIcon, borderColor }) => {
    const subtitleMap: Record<string, string> = {
        'Total Assigned': 'Awaiting radiologist action',
        'Pending': 'Queued for review',
        'In Progress': 'Currently being read',
        'Completed': 'Reports finalized',
        'New': 'Newly arrived studies',
        'Hold': 'Flagged — requires attention',
    };
    const subtitle = subtitleMap[title] ?? 'Workload statistics';

    return (
        <div
            className="group relative overflow-hidden rounded-[var(--v2-radius-xl)] cursor-default transition-all duration-[var(--v2-duration-normal)] hover:shadow-[var(--v2-shadow-card-hover)] hover:-translate-y-0.5"
            style={{
                background: 'var(--v2-bg-card)',
                border: '1px solid var(--v2-border-card)',
                boxShadow: 'var(--v2-shadow-card)',
            }}
        >
            {/* Top accent bar */}
            <div className="absolute inset-x-0 top-0 h-[3px]"
                style={{ background: `linear-gradient(90deg, ${borderColor}, ${borderColor}88)` }} />
            {/* Subtle glow orb */}
            <div aria-hidden className="absolute -bottom-5 -right-5 w-[70px] h-[70px] rounded-full pointer-events-none blur-[20px]"
                style={{ background: `${borderColor}18` }} />

            {/* ── Compact horizontal row: icon | label+sub | value ── */}
            <div className="relative flex items-center gap-3 px-4 py-3.5">
                {/* Icon */}
                <div
                    className="w-[34px] h-[34px] rounded-[var(--v2-radius-md)] flex items-center justify-center flex-shrink-0"
                    style={{
                        background: `${borderColor}15`,
                        border: `1px solid ${borderColor}30`,
                        color: borderColor,
                    }}
                >
                    <CardIcon className="h-[15px] w-[15px]" strokeWidth={2} />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] leading-none"
                        style={{ color: 'var(--v2-text-muted)' }}>
                        {title}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: 'var(--v2-text-muted)', opacity: 0.65 }}>
                        {subtitle}
                    </div>
                </div>

                {/* Value — right-aligned, colored */}
                <div className="text-[1.8rem] font-black tracking-[-0.05em] leading-none tabular-nums flex-shrink-0"
                    style={{ color: borderColor }}>
                    {value}
                </div>
            </div>

            {/* hover bottom bar */}
            <div className="absolute bottom-0 inset-x-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `linear-gradient(90deg, ${borderColor}, ${borderColor}88)` }} />
        </div>
    );
};

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="px-3 py-2 rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
                style={{
                    background: 'var(--v2-bg-card)',
                    border: '1px solid var(--v2-border-subtle)',
                    color: 'var(--v2-text-primary)',
                    outline: 'none',
                    whiteSpace: 'nowrap',
                    fontSize: 13,
                }}
            >
                <p style={{ fontWeight: 600, marginBottom: 2, color: 'var(--v2-text-heading)' }}>{payload[0].name}</p>
                <p style={{ fontWeight: 500, color: 'var(--v2-text-secondary)' }}>{payload[0].value} studies</p>
            </div>
        );
    }
    return null;
};

const BarTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="px-3.5 py-2.5 rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
                style={{
                    background: 'var(--v2-bg-card)',
                    border: '1px solid var(--v2-border-subtle)',
                    color: 'var(--v2-text-primary)',
                    outline: 'none',
                    whiteSpace: 'nowrap',
                    fontSize: 13,
                }}
            >
                <p style={{ fontWeight: 700, marginBottom: 6, color: 'var(--v2-text-heading)' }}>{label}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} style={{ color: entry.color, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color, flexShrink: 0 }} />
                        <span style={{ textTransform: 'capitalize', fontWeight: 500, color: 'var(--v2-text-secondary)' }}>{entry.name.replace('_', ' ')}:</span>
                        <span style={{ fontWeight: 700, marginLeft: 'auto', paddingLeft: 12, color: 'var(--v2-text-heading)' }}>{entry.value}</span>
                    </p>
                ))}
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--v2-border-subtle)' }}>
                    <p style={{ fontWeight: 700, color: 'var(--v2-text-heading)' }}>
                        Total: {payload.reduce((sum: number, entry: any) => sum + entry.value, 0)}
                    </p>
                </div>
            </div>
        );
    }
    return null;
};

interface RadiologistStatsProps {
    radiologistId?: string;
    radiologistName?: string;
}

const RadiologistStats: React.FC<RadiologistStatsProps> = ({ radiologistId, radiologistName }) => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<RadiologistStatsType | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await ApiServices.getStats(radiologistId || "");
                if (!alive) return;

                setStats(data);
                setLoading(false);
            } catch (e: any) {
                if (!alive) return;
                console.error("Failed to fetch radiologist stats:", e);
                setError(e?.message || "Failed to load statistics");
                setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    // Prepare data for charts - show all status types
    const workloadData = stats
        ? [
            { name: "Assigned", value: stats.totalAssigned, color: COLORS.assigned },
            { name: "Pending", value: stats.totalPending, color: COLORS.pending },
            { name: "In Progress", value: stats.totalInProgress, color: COLORS.in_progress },
            { name: "Completed", value: stats.totalCompleted, color: COLORS.completed },
            { name: "New", value: stats.totalNew, color: COLORS.new },
            { name: "Hold", value: stats.totalHold, color: COLORS.hold },
        ].filter(item => item.value > 0) // Only show statuses with values > 0
        : [];

    // Parse pending modality data from string format (e.g., "ct-1,mr-2")
    const pendingByModalityData = stats
        ? parseModalityString(stats.byModality.pending).map(item => ({
            name: item.modality,
            value: item.count,
        }))
        : [];

    // Parse all modality statuses for breakdown chart
    const modalityBreakdownData = stats
        ? (() => {
            const allModalities = new Set<string>();
            const modalityMap = new Map<string, {
                assigned: number;
                pending: number;
                in_progress: number;
                completed: number;
                new: number;
                hold: number;
            }>();

            // Initialize default values
            const initModality = (modality: string) => {
                if (!modalityMap.has(modality)) {
                    modalityMap.set(modality, {
                        assigned: 0,
                        pending: 0,
                        in_progress: 0,
                        completed: 0,
                        new: 0,
                        hold: 0
                    });
                }
            };

            // Parse all status types
            parseModalityString(stats.byModality.assigned).forEach(item => {
                allModalities.add(item.modality);
                initModality(item.modality);
                modalityMap.get(item.modality)!.assigned = item.count;
            });

            parseModalityString(stats.byModality.pending).forEach(item => {
                allModalities.add(item.modality);
                initModality(item.modality);
                modalityMap.get(item.modality)!.pending = item.count;
            });

            parseModalityString(stats.byModality.in_progress).forEach(item => {
                allModalities.add(item.modality);
                initModality(item.modality);
                modalityMap.get(item.modality)!.in_progress = item.count;
            });

            parseModalityString(stats.byModality.completed).forEach(item => {
                allModalities.add(item.modality);
                initModality(item.modality);
                modalityMap.get(item.modality)!.completed = item.count;
            });

            parseModalityString(stats.byModality.new).forEach(item => {
                allModalities.add(item.modality);
                initModality(item.modality);
                modalityMap.get(item.modality)!.new = item.count;
            });

            parseModalityString(stats.byModality.hold).forEach(item => {
                allModalities.add(item.modality);
                initModality(item.modality);
                modalityMap.get(item.modality)!.hold = item.count;
            });

            // Convert to array for chart
            return Array.from(allModalities).map(modality => ({
                modality,
                ...modalityMap.get(modality)!,
            }));
        })()
        : [];

    return (
        <section aria-labelledby="radiologist-workload-title" className="space-y-3">
            {/* Section Header */}
            <div className="flex items-center justify-between">
                <h2
                    id="radiologist-workload-title"
                    className="text-[16px] font-bold tracking-[-0.02em]"
                    style={{ color: 'var(--v2-text-heading)' }}
                >
                    {radiologistName ? 'Workload Statistics' : radiologistId ? 'Radiologist Workload' : 'Workload Statistics'}
                </h2>
            </div>

            {/* Stat Cards Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {loading ? (
                    <>
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                    </>
                ) : (
                    <>
                        <StatCard title="Total Assigned" value={stats?.totalAssigned ?? 0} icon={Icon.Activity} borderColor={COLORS.assigned} />
                        <StatCard title="Pending" value={stats?.totalPending ?? 0} icon={Icon.Clock} borderColor={COLORS.pending} />
                        <StatCard title="In Progress" value={stats?.totalInProgress ?? 0} icon={Icon.Activity} borderColor={COLORS.in_progress} />
                        <StatCard title="Completed" value={stats?.totalCompleted ?? 0} icon={Icon.CheckCircle2} borderColor={COLORS.completed} />
                        <StatCard title="New" value={stats?.totalNew ?? 0} icon={Icon.Activity} borderColor={COLORS.new} />
                        <StatCard title="Hold" value={stats?.totalHold ?? 0} icon={Icon.Clock} borderColor={COLORS.hold} />
                    </>
                )}
            </div>

            {/* Charts Row 1: Donut + Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Overall Workload Status */}
                <div
                    className="rounded-[var(--v2-radius-md)] p-4"
                    style={{
                        background: 'var(--v2-bg-card-inner)',
                        border: '1px solid var(--v2-border-glass-strong)',
                        boxShadow: 'var(--v2-shadow-card)',
                    }}
                >
                    <h3
                        className="text-[14.5px] font-bold mb-3 tracking-[-0.01em]"
                        style={{ color: 'var(--v2-text-heading)' }}
                    >
                        Overall Workload Status
                    </h3>
                    {loading ? (
                        <ChartSkeleton height="h-44" type="donut" />
                    ) : workloadData.length > 0 ? (
                        <Chart.ResponsiveContainer width="100%" height={210}>
                            <Chart.PieChart>
                                <Chart.Pie
                                    data={workloadData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={48}
                                    outerRadius={76}
                                    paddingAngle={3}
                                    dataKey="value"
                                >
                                    {workloadData.map((entry: any, index: number) => (
                                        <Chart.Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Chart.Pie>
                                <Chart.Tooltip content={<CustomTooltip />} isAnimationActive={false} />
                                <Chart.Legend
                                    verticalAlign="top"
                                    height={32}
                                    iconType="circle"
                                    iconSize={8}
                                    formatter={(value: string) => (
                                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--v2-text-secondary)' }}>{value}</span>
                                    )}
                                />
                            </Chart.PieChart>
                        </Chart.ResponsiveContainer>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-44" style={{ color: 'var(--v2-text-muted)' }}>
                            <svg className="h-10 w-10 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p className="text-[13px] font-medium">No data available</p>
                            <p className="text-[12px] mt-0.5 opacity-70">Statistics will appear once data is loaded</p>
                        </div>
                    )}
                </div>

                {/* Pending Tasks by Modality */}
                <div
                    className="rounded-[var(--v2-radius-md)] p-4"
                    style={{
                        background: 'var(--v2-bg-card-inner)',
                        border: '1px solid var(--v2-border-glass-strong)',
                        boxShadow: 'var(--v2-shadow-card)',
                    }}
                >
                    <h3
                        className="text-[14.5px] font-bold mb-3 tracking-[-0.01em]"
                        style={{ color: 'var(--v2-text-heading)' }}
                    >
                        Pending Tasks by Modality
                    </h3>
                    {loading ? (
                        <ChartSkeleton height="h-44" type="donut" />
                    ) : pendingByModalityData.length > 0 ? (
                        <>
                            <Chart.ResponsiveContainer width="100%" height={210}>
                                <Chart.PieChart>
                                    <Chart.Pie
                                        data={pendingByModalityData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={76}
                                        dataKey="value"
                                    >
                                        {pendingByModalityData.map((entry: any, index: number) => (
                                            <Chart.Cell key={`cell-${index}`} fill={MODALITY_COLORS[entry.name] ?? '#6B7280'} />
                                        ))}
                                    </Chart.Pie>
                                    <Chart.Tooltip content={<CustomTooltip />} isAnimationActive={false} />
                                    <Chart.Legend
                                        verticalAlign="top"
                                        height={32}
                                        iconType="circle"
                                        iconSize={8}
                                        formatter={(value: string) => (
                                            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--v2-text-secondary)' }}>{value}</span>
                                        )}
                                    />
                                </Chart.PieChart>
                            </Chart.ResponsiveContainer>
                            <p className="text-[12px] font-medium text-center mt-1" style={{ color: 'var(--v2-text-muted)' }}>
                                Modalities with the most backlog
                            </p>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-44" style={{ color: 'var(--v2-text-muted)' }}>
                            <p className="text-[13px]">No pending tasks</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Charts Row 2: Stacked Bar */}
            <div
                className="rounded-[var(--v2-radius-md)] p-4"
                style={{
                    background: 'var(--v2-bg-card-inner)',
                    border: '1px solid var(--v2-border-glass-strong)',
                    boxShadow: 'var(--v2-shadow-card)',
                }}
            >
                {/* Chart header with title + legend inline */}
                <div className="flex items-center justify-between mb-4">
                    <h3
                        className="text-[14.5px] font-bold tracking-[-0.01em]"
                        style={{ color: 'var(--v2-text-heading)' }}
                    >
                        Breakdown by Modality
                    </h3>
                    {/* Inline status legend */}
                    {!loading && modalityBreakdownData.length > 0 && (
                        <div className="flex items-center gap-3 flex-wrap justify-end">
                            {[
                                { key: 'assigned', label: 'Assigned', color: COLORS.assigned },
                                { key: 'pending', label: 'Pending', color: COLORS.pending },
                                { key: 'in_progress', label: 'In Progress', color: COLORS.in_progress },
                                { key: 'completed', label: 'Completed', color: COLORS.completed },
                                { key: 'new', label: 'New', color: COLORS.new },
                                { key: 'hold', label: 'Hold', color: COLORS.hold },
                            ].map(item => (
                                <span key={item.key} className="inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: item.color }} />
                                    <span className="text-[12px] font-medium capitalize" style={{ color: 'var(--v2-text-secondary)' }}>{item.label}</span>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                {loading ? (
                    <ChartSkeleton height="h-52" type="bar" />
                ) : modalityBreakdownData.length > 0 ? (
                    <Chart.ResponsiveContainer width="100%" height={260}>
                        <Chart.BarChart
                            data={modalityBreakdownData}
                            margin={{ top: 4, right: 20, left: 0, bottom: 8 }}
                            barSize={28}
                            barCategoryGap="35%"
                        >
                            <Chart.XAxis
                                dataKey="modality"
                                tick={{ fill: 'var(--chart-axis)', fontSize: 12, fontWeight: 600 }}
                                axisLine={{ stroke: 'var(--chart-grid)' }}
                                tickLine={false}
                            />
                            <Chart.YAxis
                                tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                width={32}
                                allowDecimals={false}
                            />
                            <Chart.Tooltip content={<BarTooltip />} isAnimationActive={false} cursor={{ fill: 'var(--v2-bg-inset)', radius: 4 }} />
                            <Chart.Bar dataKey="assigned" stackId="a" fill={COLORS.assigned} radius={[0, 0, 0, 0]} isAnimationActive={false} />
                            <Chart.Bar dataKey="pending" stackId="a" fill={COLORS.pending} radius={[0, 0, 0, 0]} isAnimationActive={false} />
                            <Chart.Bar dataKey="in_progress" stackId="a" fill={COLORS.in_progress} radius={[0, 0, 0, 0]} isAnimationActive={false} />
                            <Chart.Bar dataKey="completed" stackId="a" fill={COLORS.completed} radius={[0, 0, 0, 0]} isAnimationActive={false} />
                            <Chart.Bar dataKey="new" stackId="a" fill={COLORS.new} radius={[0, 0, 0, 0]} isAnimationActive={false} />
                            <Chart.Bar dataKey="hold" stackId="a" fill={COLORS.hold} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                        </Chart.BarChart>
                    </Chart.ResponsiveContainer>
                ) : (
                    <div className="flex flex-col items-center justify-center h-52" style={{ color: 'var(--v2-text-muted)' }}>
                        <Icon.Activity className="h-10 w-10 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </Icon.Activity>
                        <p className="text-[13px] font-medium">No modality data</p>
                        <p className="text-[12px] mt-0.5 opacity-70">Breakdown appears once studies are assigned</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default RadiologistStats;
